import { Injectable } from '@angular/core';
import { ServiceItem } from '../models/service-item.model';
import { ServiceCategory } from '../models/service-category.model';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Choice } from '../models/choice.model';
import { Expert } from '../models/expert.model';
import { ExpertsService } from './experts.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ServiceService {
  private dataUrl: string = 'services/full/';
  private services: ServiceCategory[] = [];

  constructor(
    private readonly http: HttpClient,
    private expertsService: ExpertsService,
    private router: Router,
  ) {}

  getAllServices() {
    return this.services.slice();
  }

  getCategoryandServices(categoryId: number): ServiceCategory | undefined {
    return this.services.find((category) => category.id === categoryId);
  }

  storeServices(services: ServiceCategory[]) {
    this.services = services;
  }

  getService(id: number): ServiceItem | undefined {
    let service: ServiceItem | undefined = undefined;

    for (const category of this.services) {
      service = category.serviceItems.find((service) => service.id === id);
      if (service) {
        break;
      }
    }
    return service;
  }

  getChoice(id: number): Choice | undefined {
    for (const category of this.services) {
      for (const service of category.serviceItems) {
        const choice = service.choices?.find((choice) => choice.id === id);
        if (choice) {
          return choice;
        }
      }
    }
    return undefined;
  }

  /** Price and duration for a choice and expert; each falls back to the choice default on its own. */
  resolveChoice(choice: Choice, expertId?: number): Choice {
    const override = expertId ? choice.expertOverrides?.find((entry) => entry.expertId === expertId) : undefined;

    if (!override) {
      return choice;
    }

    const price = override.price ?? choice.price;
    const workDuration = override.workDuration ?? choice.workDuration;
    const processingDuration = override.processingDuration ?? choice.processingDuration;
    const finishingDuration = override.finishingDuration ?? choice.finishingDuration;

    if (workDuration == null && processingDuration == null && finishingDuration == null) {
      return { ...choice, price, duration: override.duration ?? choice.duration };
    }

    // an unsplit choice is all work, so an expert can add a gap to one by overriding a single part
    const work = workDuration ?? choice.duration;
    const processing = processingDuration ?? 0;
    const finishing = finishingDuration ?? 0;

    return {
      ...choice,
      price,
      duration: work + processing + finishing,
      workDuration: work,
      processingDuration: processing,
      finishingDuration: finishing,
    };
  }

  /** Cheapest bookable choice. Without an expert, the "from" price across all experts. */
  getMinChoice(serviceId: number, expertId?: number): Choice {
    const service = this.getService(serviceId);
    const choices = service?.choices ?? [];

    if (!choices.length) {
      return { id: null, name: null, duration: null, price: 0 };
    }

    const expertIds = expertId
      ? [expertId]
      : (service.experts2Services ?? []).map((expert2service) => expert2service.expertId);

    const candidates = expertIds.length
      ? choices.flatMap((choice) => expertIds.map((id) => this.resolveChoice(choice, id)))
      : choices;

    return candidates.reduce((minChoice, choice) => (choice.price < minChoice.price ? choice : minChoice));
  }

  // getAddon(id: number): Addon | undefined {
  //   let addon;
  //   for (let category of this.services) {
  //     for (let service of category.serviceItems) {
  //       addon = service.addons?.filter((addon) => addon.id === id);
  //       if (addon) {
  //         break;
  //       }
  //     }
  //   }
  //   return addon;
  // }

  // getAddonsForService(serviceItemId: number): ServiceItem[] {
  //   const service = this.getService(serviceItemId);
  //   let addons: ServiceItem[] = [];
  //   for (let addon of service.addons) {
  //     addons.push(this.getService(addon.addonServiceItemId));
  //   }
  //   console.log('Addons for service id ', serviceItemId, addons);
  //   return addons;
  // }

  fetchServices(locationUuid: string): Observable<ServiceCategory[]> {
    return this.http.get<ServiceCategory[]>(this.dataUrl + locationUuid).pipe(
      tap((services) => {
        services.forEach((category) => {
          category.serviceItems.forEach((item) => {
            this.text2Html(item);
          });
        });
        this.storeServices(services);
      }),
      catchError(() => {
        this.router.navigate(['/page-not-found']);
        return of([]);
      }),
    );
  }

  text2Html(serviceItem: ServiceItem): ServiceItem {
    if (serviceItem.description) {
      serviceItem.description = serviceItem.description.replace(/(?:\r\n|\r|\n)/g, '<br>');
    }
    return serviceItem;
  }

  getExpertsForService(service: ServiceItem, includeAnyone = true): Expert[] {
    const experts: Expert[] = [];

    if (includeAnyone) {
      // TODO: implement this after MVP release
      // experts.push({ id: 0, firstName: 'Anyone', lastName: '' });
    }

    if (service.experts2Services) {
      experts.push(
        ...service.experts2Services
          .map((expert2service) => this.expertsService.getExpertDetails(expert2service.expertId))
          .filter((expert): expert is Expert => expert !== undefined)
          .sort((a, b) => (a.orderId ?? Number.MAX_SAFE_INTEGER) - (b.orderId ?? Number.MAX_SAFE_INTEGER)),
      );
    }
    return experts;
  }
}
